import { ISOCountryCode } from '@pmi.web/countries';
import {
  DateInput,
  DropdownInput,
  EmailInput,
  Fieldset,
  PhoneInput,
  TextInput,
} from '@pmi.web/react-inputs';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IAddress } from '../../../types/IAddress';
import { calculateYearsUntilToday } from '../../../utils/dates';
import { AddressInput } from '../../common/address-input/AddressInput';
import { Form } from '../../common/Form';
import { IFormProps } from '../../common/Form.types';

export interface IProspectCreationFormData {
  readonly sponsorId?: string;
  readonly id?: string;
  readonly email?: string;
  readonly salutation?: 'MR' | 'MRS';
  readonly givenName?: string;
  readonly familyName?: string;
  readonly dateOfBirth?: string;
  readonly phoneNumber?: string;
  readonly address?: IAddress;
}

export function ProspectCreationForm(
  props: IFormProps<IProspectCreationFormData>,
) {
  const {
    formId,
    country,
    language,
    defaultValues = {},
    businessLogicErrors = {},
    onSubmit,
  } = props;

  return (
    <Form<IProspectCreationFormData>
      formId={formId}
      language={language}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      businessLogicErrors={businessLogicErrors}
    >
      <ProspectCreationFormContent country={country} />
    </Form>
  );
}

const MIN_AGE_REQUIRED = 18;

interface IProspectCreationFormContentProps {
  readonly country: ISOCountryCode;
}

function ProspectCreationFormContent({
  country,
}: IProspectCreationFormContentProps) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const env = useMemo(
    () =>
      window?.location?.hostname.endsWith('.com') ?? false
        ? 'production'
        : 'development',
    [],
  );

  return (
    <>
      <Fieldset title={t('Personal Details')}>
        <EmailInput
          required
          {...register('email', {
            required: t('This field is required.'),
          })}
          label={t('Email address')}
          errors={
            errors['email'] ? [errors['email'].message?.toString() ?? ''] : []
          }
        />
        <DropdownInput
          {...register('salutation')}
          label={t('Salutation')}
          options={[
            { value: '', label: '' },
            { value: 'MR', label: t('Mr') },
            { value: 'MRS', label: t('Mrs') },
          ]}
          errors={
            errors['salutation']
              ? [errors['salutation'].message?.toString() ?? '']
              : []
          }
        />

        <TextInput
          required
          {...register('givenName', {
            required: t('This field is required.'),
          })}
          label={t('First Name')}
          errors={
            errors['givenName']
              ? [errors['givenName'].message?.toString() ?? '']
              : []
          }
        />

        <TextInput
          {...register('familyName')}
          label={t('Last Name')}
          errors={
            errors['familyName']
              ? [errors['familyName'].message?.toString() ?? '']
              : []
          }
        />

        <DateInput
          {...register('dateOfBirth', {
            validate: {
              minAge: value => {
                if (
                  calculateYearsUntilToday(new Date(value)) < MIN_AGE_REQUIRED
                ) {
                  return t('You must be at least {{age}} years old', {
                    age: MIN_AGE_REQUIRED,
                  });
                }

                return undefined;
              },
            },
          })}
          label={t('Date of Birth')}
          errors={
            errors['dateOfBirth']
              ? [errors['dateOfBirth'].message?.toString() ?? '']
              : []
          }
        />

        <PhoneInput
          {...register('phoneNumber')}
          label={t('Phone Number')}
          errors={
            errors['phoneNumber']
              ? [errors['phoneNumber'].message?.toString() ?? '']
              : []
          }
        />
      </Fieldset>

      <Fieldset title={t('Address')}>
        <AddressInput country={country} env={env} />
      </Fieldset>
    </>
  );
}
